import { Select, Tabs, createListCollection, Portal } from "@ark-ui/react";
import { useState, useMemo } from "react";
import { iconNames } from "lucide-react/dynamic";
import { useField } from "react-final-form";

import Icon, { customIconNames, IconNames } from "@/components/icon/icon";
import Stack from "@/components/stack/stack";

import styles from "./icon-selector.module.css";

interface IconSelectorProps {
  name: string;
  label?: string;
}

export default function IconSelector({ name, label }: IconSelectorProps) {
  const [searchQuery, setSearchQuery] = useState("");

  const [filteredDynamicIcons, filteredCustomIcons] = useMemo(() => {
    const dynamicIcons = iconNames.filter((icon) =>
      icon.toLowerCase().includes(searchQuery.toLowerCase()),
    );
    const customIcons = customIconNames.filter((icon) =>
      icon.toLowerCase().includes(searchQuery.toLowerCase()),
    );
    return [dynamicIcons, customIcons];
  }, [searchQuery]);

  const collection = createListCollection({
    items: [...filteredDynamicIcons, ...filteredCustomIcons],
  });

  const { input } = useField(name);

  return (
    <Select.Root
      className={styles.root}
      collection={collection}
      positioning={{
        sameWidth: true,
      }}
      onValueChange={(details) => input.onChange(details.value[0])}
    >
      {label && <Select.Label className={styles.label}>{label}</Select.Label>}
      <Select.Trigger className={styles.trigger}>
        <Select.ValueText placeholder="Select an icon">
          {input.value && (
            <div className={styles.value}>
              <Icon name={input.value as IconNames} size={24} />
              {input.value}
            </div>
          )}
        </Select.ValueText>
      </Select.Trigger>

      <Portal>
        <Select.Positioner>
          <Select.Content className={styles.selectContent}>
            <div className={styles.search}>
              <input
                type="text"
                placeholder="Search icons..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className={styles.input}
              />
            </div>

            <Tabs.Root defaultValue="custom">
              <Tabs.List className={styles.tabs}>
                <Tabs.Trigger value="custom" className={styles.tab}>
                  KNMI Icons ({filteredCustomIcons.length})
                </Tabs.Trigger>
                <Tabs.Trigger value="dynamic" className={styles.tab}>
                  Dynamic Icons ({filteredDynamicIcons.length})
                </Tabs.Trigger>
              </Tabs.List>

              <Tabs.Content value="dynamic">
                <Select.ItemGroup>
                  {filteredDynamicIcons.length === 0 ? (
                    <div style={{ padding: "8px", color: "#666", fontStyle: "italic" }}>
                      No icons found
                    </div>
                  ) : (
                    filteredDynamicIcons.map((iconName) => (
                      <Select.Item key={iconName} item={iconName}>
                        <Select.ItemText>
                          <Stack gap={8} alignItems="center">
                            <Icon name={iconName} size={16} />
                            <span>{iconName}</span>
                          </Stack>
                        </Select.ItemText>
                      </Select.Item>
                    ))
                  )}
                </Select.ItemGroup>
              </Tabs.Content>

              <Tabs.Content value="custom">
                <Select.ItemGroup>
                  {filteredCustomIcons.length === 0 ? (
                    <div style={{ padding: "8px", color: "#666", fontStyle: "italic" }}>
                      No icons found
                    </div>
                  ) : (
                    filteredCustomIcons.map((iconName) => (
                      <Select.Item key={iconName} item={iconName}>
                        <Select.ItemText>
                          <Stack gap={8} alignItems="center">
                            <Icon name={iconName} size={16} />
                            <span>{iconName}</span>
                          </Stack>
                        </Select.ItemText>
                      </Select.Item>
                    ))
                  )}
                </Select.ItemGroup>
              </Tabs.Content>
            </Tabs.Root>
          </Select.Content>
        </Select.Positioner>
      </Portal>
    </Select.Root>
  );
}
